import { useCallback, useEffect, useState } from "react";
import { getWorkspaceLifecycle } from "../services/server";

/**
 * Per-page hook that fetches the workspace's lifecycle metadata.
 * Powers `<WorkspaceLockOverlay>`, `<WorkspaceCloseBanner>` and
 * `<WorkspaceStatusBadge>` on every page that needs them.
 *
 * The URL key may be either a slug or a legacy orgnr — both resolve
 * server-side via OrganisationWorkspace.objects.for_user(...).filter(slug=...).
 * Orgnrs without a backfilled slug just return 404 here; the calling
 * page should gracefully render its content without the overlay
 * (workspace data missing isn't a reason to block UX).
 *
 * Also listens for `billing:setup-succeeded` events so the overlay
 * refreshes after the broker adds a card via the global popup.
 */
export default function useWorkspaceLifecycle(slugOrKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(() => {
    if (!slugOrKey) return;
    setLoading(true);
    getWorkspaceLifecycle(slugOrKey)
      .then((d) => { setData(d); setError(null); })
      .catch((err) => { setError(err); setData(null); })
      .finally(() => setLoading(false));
  }, [slugOrKey]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    function onSetup() { reload(); }
    window.addEventListener("billing:setup-succeeded", onSetup);
    return () => window.removeEventListener("billing:setup-succeeded", onSetup);
  }, [reload]);

  return { workspace: data, loading, error, reload };
}
